import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { BedDouble, CalendarCheck, Euro, LogOut, Plus, Pencil, Trash2, Clock, Eye, FileText, Mail, Search, Globe, BookOpen } from "lucide-react";
import { api, eur, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import AdminContent from "@/pages/AdminContent";

const CATEGORIES = ["Einzelzimmer", "Doppelzimmer", "Business", "Komfort", "Suite", "Familienzimmer"];
const EMPTY_ROOM = { name: "", category: "Doppelzimmer", description: "", price_per_night: 99, capacity: 2, size_sqm: 24, amenities: "", image_url: "", active: true };

const STATUS_LABEL = { pending: "Offen", confirmed: "Bestätigt", cancelled: "Storniert" };
const STATUS_STYLE = {
  pending: "bg-gold/15 text-terra",
  confirmed: "bg-forest/10 text-forest",
  cancelled: "bg-destructive/10 text-destructive",
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("home");
  const [stats, setStats] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_ROOM);
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");

  const load = useCallback(async () => {
    try {
      const [s, r, b] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/rooms"),
        api.get("/admin/bookings"),
      ]);
      setStats(s.data);
      setRooms(r.data);
      setBookings(b.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("hw_token");
        navigate("/admin/login");
      } else toast.error(formatApiError(e));
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("hw_token")) return navigate("/admin/login");
    load();
  }, [load, navigate]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_ROOM); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r, amenities: (r.amenities || []).join(", ") });
    setDialogOpen(true);
  };

  const saveRoom = async () => {
    const payload = { ...form, price_per_night: Number(form.price_per_night), capacity: Number(form.capacity), size_sqm: Number(form.size_sqm), amenities: form.amenities.split(",").map((a) => a.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/admin/rooms/${editing.id}`, payload);
      else await api.post("/admin/rooms", payload);
      toast.success(editing ? "Zimmer aktualisiert" : "Zimmer angelegt");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteRoom = async (r) => {
    if (!window.confirm(`„${r.name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/rooms/${r.id}`);
      toast.success("Zimmer gelöscht");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (b, status) => {
    try {
      await api.patch(`/admin/bookings/${b.id}`, { status });
      toast.success(status === "cancelled" ? "Buchung storniert — Gast wurde per Mail informiert" : `Buchung ${STATUS_LABEL[status].toLowerCase()}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resend = async (b) => {
    try {
      await api.post(`/admin/bookings/${b.id}/resend`);
      toast.success(`Bestätigung erneut an ${b.guest_email} gesendet`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredBookings = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter((b) => {
      if (statusFilter !== "alle" && b.status !== statusFilter) return false;
      if (!q) return true;
      return [b.reference, b.guest_name, b.guest_email, b.room_name].join(" ").toLowerCase().includes(q);
    });
  }, [bookings, search, statusFilter]);

  const logout = () => { localStorage.removeItem("hw_token"); navigate("/admin/login"); };

  const TABS = [["home", "Übersicht"], ["buchungen", "Buchungen"], ["zimmer", "Zimmer & Preise"], ["inhalte", "Website-Inhalte"]];

  return (
    <main className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-forest-dark text-sand sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Hotel Wildpferd" className="h-10 w-auto rounded-lg bg-sand px-1.5 py-0.5" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-sand/60 hover:text-gold transition-colors" data-testid="admin-view-site-link">Website ansehen</Link>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 text-xs font-semibold hover:text-gold transition-colors">
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
            {[
              [CalendarCheck, "Buchungen gesamt", stats.bookings_total],
              [Clock, "Offen", stats.bookings_pending],
              [Euro, "Umsatz", eur(stats.revenue)],
              [BedDouble, "Zimmerkategorien", stats.rooms],
            ].map(([Icon, label, value]) => (
              <div key={label} className="bg-white rounded-2xl border border-forest/10 p-5">
                <Icon size={18} className="text-terra" />
                <p className="mt-3 font-serif text-2xl sm:text-3xl font-semibold text-forest">{value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45">{label}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${tab === key ? "bg-forest text-sand" : "bg-white text-ink/60 border border-forest/10 hover:border-forest/30"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "home" && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="admin-home">
            {[
              { icon: Globe, title: "Website-Editor", text: "Texte, Fotos, Kontaktdaten, FAQ, Bankdaten und Rechtstexte ändern — ohne Code.", tab: "inhalte", testid: "home-card-editor" },
              { icon: BookOpen, title: "Buchungsverwaltung", text: "Alle Buchungen auf einen Blick: Details, Gäste-Adressen, Status, Rechnungen und Bestätigungen.", tab: "buchungen", testid: "home-card-bookings" },
              { icon: BedDouble, title: "Zimmer & Preise", text: "Zimmerkategorien, Preise, Ausstattung und Zimmerfotos verwalten.", tab: "zimmer", testid: "home-card-rooms" },
            ].map((card) => (
              <button key={card.title} onClick={() => setTab(card.tab)} data-testid={card.testid}
                className="group text-left bg-white rounded-3xl border border-forest/10 p-8 hover:border-gold/60 hover:shadow-xl hover:shadow-forest/5 transition-[border-color,box-shadow] duration-300">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest text-sand group-hover:bg-terra transition-colors">
                  <card.icon size={22} />
                </span>
                <h3 className="mt-5 font-serif text-2xl font-semibold text-forest">{card.title}</h3>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed">{card.text}</p>
                <p className="mt-4 text-xs font-semibold text-terra">Öffnen →</p>
              </button>
            ))}
          </div>
        )}

        {tab === "buchungen" && (
          <div className="mt-6" data-testid="admin-bookings-panel">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative flex-1 max-w-sm">
                <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bookings-search-input"
                  placeholder="Suchen: Name, Nr., Zimmer …"
                  className="w-full rounded-full border border-forest/15 bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-gold" />
              </div>
              <div className="flex gap-2">
                {[["alle", "Alle"], ["pending", "Offen"], ["confirmed", "Bestätigt"], ["cancelled", "Storniert"]].map(([v, l]) => (
                  <button key={v} onClick={() => setStatusFilter(v)} data-testid={`bookings-filter-${v}`}
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${statusFilter === v ? "bg-forest text-sand" : "bg-white border border-forest/10 text-ink/60 hover:border-forest/30"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 bg-white rounded-2xl border border-forest/10 overflow-hidden" data-testid="admin-bookings-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-forest/10 text-left font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45">
                      <th className="px-5 py-4">Nr.</th><th className="px-5 py-4">Gast</th><th className="px-5 py-4">Zimmer</th>
                      <th className="px-5 py-4">Zeitraum</th><th className="px-5 py-4">Betrag</th><th className="px-5 py-4">Zahlung</th>
                      <th className="px-5 py-4">Status</th><th className="px-5 py-4">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((b) => (
                      <tr key={b.id} className="border-b border-forest/5 hover:bg-cream/50 transition-colors" data-testid={`booking-row-${b.reference}`}>
                        <td className="px-5 py-4 font-mono text-xs">{b.reference}</td>
                        <td className="px-5 py-4"><p className="font-semibold text-forest">{b.guest_name}</p><p className="text-xs text-ink/45">{b.guest_email}</p></td>
                        <td className="px-5 py-4">{b.rooms_count > 1 ? `${b.rooms_count} × ` : ""}{b.room_name}</td>
                        <td className="px-5 py-4 whitespace-nowrap">{fmtDate(b.check_in)} – {fmtDate(b.check_out)}<span className="text-xs text-ink/45"> · {b.nights} N.</span></td>
                        <td className="px-5 py-4 font-semibold">{eur(b.total_price)}</td>
                        <td className="px-5 py-4">
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${b.payment_status === "paid" ? "bg-forest/10 text-forest" : "bg-gold/15 text-terra"}`}>
                            {b.payment_method === "stripe" ? (b.payment_status === "paid" ? "Online bezahlt" : "Online offen") : "Überweisung"}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <select value={b.status} data-testid={`booking-status-${b.reference}`} onChange={(e) => setStatus(b, e.target.value)}
                            className={`rounded-full px-3 py-1.5 text-[11px] font-semibold outline-none cursor-pointer ${STATUS_STYLE[b.status]}`}>
                            {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex gap-1.5">
                            <button onClick={() => setDetail(b)} title="Details & Adresse" data-testid={`booking-detail-${b.reference}`}
                              className="rounded-full border border-forest/15 p-2 text-forest hover:bg-forest hover:text-sand transition-colors">
                              <Eye size={14} />
                            </button>
                            <Link to={`/admin/rechnung/${b.id}`} title="Rechnung" data-testid={`booking-invoice-${b.reference}`}
                              className="rounded-full border border-forest/15 p-2 text-forest hover:bg-forest hover:text-sand transition-colors">
                              <FileText size={14} />
                            </Link>
                            <button onClick={() => resend(b)} title="Bestätigung erneut senden" data-testid={`booking-resend-${b.reference}`}
                              className="rounded-full border border-forest/15 p-2 text-forest hover:bg-forest hover:text-sand transition-colors">
                              <Mail size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredBookings.length === 0 && (
                      <tr><td colSpan={8} className="px-5 py-12 text-center text-ink/45" data-testid="bookings-empty">Keine Buchungen gefunden.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {tab === "inhalte" && <AdminContent />}

        {tab === "zimmer" && (
          <div className="mt-6" data-testid="admin-rooms-panel">
            <button onClick={openCreate} data-testid="admin-add-room-btn"
              className="flex items-center gap-2 rounded-full bg-forest text-sand px-6 py-3 text-sm font-semibold hover:bg-forest-dark transition-colors">
              <Plus size={16} /> Neue Zimmerkategorie
            </button>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {rooms.map((r) => (
                <article key={r.id} className={`bg-white rounded-2xl border border-forest/10 overflow-hidden ${!r.active ? "opacity-55" : ""}`} data-testid={`admin-room-card-${r.id}`}>
                  <img src={r.image_url} alt={r.name} className="h-40 w-full object-cover" />
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-serif text-xl font-semibold text-forest">{r.name}</h3>
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/45 mt-0.5">{r.category} · {r.size_sqm} m² · {r.capacity} Pers.</p>
                      </div>
                      <p className="font-serif text-xl font-semibold text-terra whitespace-nowrap">{eur(r.price_per_night)}</p>
                    </div>
                    <p className="mt-3 text-xs text-ink/55 leading-relaxed line-clamp-2">{r.description}</p>
                    <div className="mt-4 flex gap-2">
                      <button onClick={() => openEdit(r)} data-testid={`admin-edit-room-${r.id}`}
                        className="flex-1 flex items-center justify-center gap-1.5 rounded-full border border-forest/15 py-2 text-xs font-semibold text-forest hover:bg-forest hover:text-sand transition-colors">
                        <Pencil size={13} /> Bearbeiten
                      </button>
                      <button onClick={() => deleteRoom(r)} data-testid={`admin-delete-room-${r.id}`}
                        className="flex items-center justify-center rounded-full border border-destructive/25 px-3 text-destructive hover:bg-destructive hover:text-white transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-md bg-sand" data-testid="booking-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">Buchung {detail?.reference}</DialogTitle>
          </DialogHeader>
          {detail && (
            <dl className="space-y-2.5 text-sm mt-2">
              {[
                ["Gast", detail.guest_name],
                ["E-Mail", detail.guest_email],
                ["Telefon", detail.guest_phone || "—"],
                ["Adresse", [detail.guest_street, [detail.guest_zip, detail.guest_city].filter(Boolean).join(" "), detail.guest_country].filter(Boolean).join(", ") || "—"],
                ["Zimmer", `${detail.rooms_count > 1 ? `${detail.rooms_count} × ` : ""}${detail.room_name}`],
                ["Zeitraum", `${fmtDate(detail.check_in)} – ${fmtDate(detail.check_out)} · ${detail.nights} Nächte`],
                ["Personen", detail.guests],
                ["Betrag", eur(detail.total_price)],
                ["Zahlungsart", detail.payment_method === "stripe" ? "Online (Stripe)" : "Überweisung"],
                ["Zahlungsstatus", detail.payment_status === "paid" ? "Bezahlt" : "Offen"],
                ["Sonderwünsche", detail.special_requests || "—"],
                ["Gebucht am", fmtDate(detail.created_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-forest/5 pb-2">
                  <dt className="text-ink/50">{k}</dt><dd className="font-medium text-ink text-right">{v}</dd>
                </div>
              ))}
            </dl>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-sand" data-testid="room-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">{editing ? "Zimmer bearbeiten" : "Neue Zimmerkategorie"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <label className="col-span-2 flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">Name *</span>
              <input data-testid="room-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold" placeholder="z. B. Doppelzimmer Business" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">Kategorie</span>
              <select data-testid="room-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">Preis / Nacht (€) *</span>
              <input type="number" min="1" step="0.5" data-testid="room-form-price" value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })}
                className="rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">Personen</span>
              <input type="number" min="1" max="8" data-testid="room-form-capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                className="rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">Größe (m²)</span>
              <input type="number" min="8" data-testid="room-form-size" value={form.size_sqm} onChange={(e) => setForm({ ...form, size_sqm: e.target.value })}
                className="rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold" />
            </label>
            <label className="col-span-2 flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">Beschreibung</span>
              <textarea rows={2} data-testid="room-form-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold resize-none" />
            </label>
            <label className="col-span-2 flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">Ausstattung (kommagetrennt)</span>
              <input data-testid="room-form-amenities" value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })}
                className="rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold" placeholder="Frühstück, WLAN, Balkon" />
            </label>
            <label className="col-span-2 flex flex-col gap-1.5">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/50">Bild-URL (z. B. /images/komfort-doppel.jpg)</span>
              <input data-testid="room-form-image" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="rounded-xl border border-forest/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-gold" placeholder="/images/… oder https://…" />
            </label>
            <label className="col-span-2 flex items-center gap-3">
              <Switch data-testid="room-form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-sm text-ink/70">Auf der Website buchbar</span>
            </label>
          </div>
          <button onClick={saveRoom} data-testid="room-form-save" disabled={!form.name || !form.price_per_night}
            className="mt-4 w-full rounded-full bg-forest text-sand py-3 text-sm font-semibold hover:bg-forest-dark disabled:opacity-40 transition-colors">
            {editing ? "Änderungen speichern" : "Zimmer anlegen"}
          </button>
        </DialogContent>
      </Dialog>
    </main>
  );
}
